import { getIsSlfCoverageLongName, slfCoverageNameToLongName } from "shared/types/SlfCoverages";
import type {
  DefaultsSelection,
  BillingAdministrationType,
  BillingStructureType,
  BillingSummaryStatementType,
  BillSplitType,
  ERISAPlanNumber,
  ERISAPlanType,
  Frontloading,
  MvpBrokerTier,
  PhaseId,
  PendingCompleteNotApplicable,
  RehireProvision,
  BenefitPayableTo,
  IssueOrAttainedAge,
  Tobacco,
  EmployeeSpouse,
  ClientError,
  ClientErrorType,
  DentalRollover,
  WhoSubmitsClaims,
  QPSEvent,
} from "@prisma/client";
import type { APIJob } from "shared/types/APIJob";
import type { BenAdminPlatform } from "shared/types/BenAdminPlatform";
import type { Bill } from "shared/types/Bill";
import type { ClientUser as DomainClientUser } from "shared/types/ClientUser";
import type { Contact, ContactInput } from "shared/types/Contact";
import type { ValueOf } from "shared/types/Helper";
import type { Location } from "shared/types/Location";
import type { LocationStateCode, DisabilityStateCode } from "shared/types/Location";
import type { MonthlyClaimsReportMailingLocation } from "shared/types/MonthlyClaimsReportMailingLocation";
import type { OnboardingFormWithMetadata } from "shared/types/OnboardingForm";
import type { OutsideSigner, OutsideSignerId } from "shared/types/OutsideSigner";
import type { Plan } from "shared/types/Plan";
import type { BenAdminAndDataFeedApiConnection } from "shared/types/Policy";
import type { DomainPolicyTask } from "shared/types/PolicyTask";
import type { QPSSoldCaseCategoryGroupCode } from "shared/types/QPS";
import type {
  SlfCoverageLongName,
  CoverageNotIncludedInSection125Plan,
  PFMLCoverage,
} from "shared/types/SlfCoverages";
import type { Subsidiary } from "shared/types/Subsidiary";
import type { DomainTask } from "shared/types/Task";
import type { TicketStatus } from "shared/types/Ticket";
import type {
  BenAdmin,
  Broker,
  DomainUser,
  SimplifiedDomainUser,
  User,
  UserId,
} from "shared/types/User";
import type {
  importedBillingContactValidation,
  importedBillingPreferencesValidation,
} from "shared/validation/importedBillingPreferences";
import type { PolicyTicket, PrimaryTicket } from "shared/validation/ticket";
import type * as Yup from "yup";

export type ClientId = string;
export type PolicyId = string;

export type ClientType =
  | "LESS_THAN_150_EMPLOYEES_NON_MVP"
  | "MORE_THAN_150_EMPLOYEES_NON_MVP"
  | "LESS_THAN_300_EMPLOYEES_MVP"
  | "MORE_THAN_300_EMPLOYEES_MVP"
  | "HEALTHPRO"
  | null;

export const blockOfBusinessValues = [
  "Automatic Data Processing Insurance Agency, Inc. (ADPIA cases)",
  "Alliant (Priority National Firm)",
  "Cason Group",
  "Cheiba",
  "Cottingham and Butler Insurance Services",
  "Credit Union Consortium Block",
  "DBL Center",
  "Decisely Blocks",
  "Empyrean Implementation Process",
  "Gallagher (Priority National Firm)",
  "HEBA",
  "Hub International (Priority National Firm)",
  "IBEW",
  "ISM",
  "Johnson Financial Group Block",
  "Kansas Bankers",
  "Kelly & Associates Block Cases",
  "Liazon Implementation Process",
  "Mercer (Priority National Firm)",
  "MMA (Priority National Firm)",
  "My Quality Benefits",
  "NJPA/Sourcewell Cases",
  "Not Applicable",
  "Paychex Cases",
  "Pierce Block",
  "PlanSource Direct",
  "PlanSource Reseller",
  "PPI (Professional Pensions Inc) Cases",
  "Prime One",
  "PSBA",
  "Qualchoice",
  "Sourcewell",
  "USI (Priority National Firm)",
] as const;
export type BlockOfBusiness = ValueOf<typeof blockOfBusinessValues>;

export type YesNoNotSure = YesNo | "NOT_SURE";
export type YesNo = "YES" | "NO";
export type RehireProvisionUnit = "MONTH" | "WEEK" | "DAY";
export type ReleaseStatus = "NOT_READY" | "READY_FOR_RELEASE" | "LIVE";
export const implementationType = ["NEW_IMPLEMENTATION", "ADD_COVERAGE"] as const;
export type ImplementationType = ValueOf<typeof implementationType>;

export type EIFSubStepViewMode = "pre-sign" | "post-sign-with-edit" | "review-page";

export const yesNoValuesRecord: Record<YesNo, string> = {
  YES: "Yes",
  NO: "No",
};

export const payableToValuesRecord: Record<BenefitPayableTo, string> = {
  EMPLOYEE: "The employee",
  EMPLOYER: "The employer",
};

export const coverageChangesEffective = [
  "IMMEDIATELY",
  "FIRST_OF_THE_FOLLOWING_MONTH",
  "ANNUALLY_ON_POLICY_ANNIVERSARY",
  "NOT_APPLICABLE",
] as const;
export type CoverageChangesEffective = ValueOf<typeof coverageChangesEffective>;

export const certificateSplitByValues = ["CLASS", "LOCATION"] as const;
export type CertificateSplitBy = ValueOf<typeof certificateSplitByValues>;

export const certificateSplitByLabels: Record<CertificateSplitBy, string> = {
  CLASS: "Class",
  LOCATION: "Location",
};

export const isCertificateSplitBy = (value: unknown): value is CertificateSplitBy =>
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions -- required
  typeof value === "string" && certificateSplitByValues.includes(value as CertificateSplitBy);

export const certificateSplitTypeValues = ["ONE_CERTIFICATE", "SPLIT"] as const;
export type CertificateSplitType = ValueOf<typeof certificateSplitTypeValues>;

export const certificateSubGroupValues = ["LIFE", "ADND", "STD", "LTD"] as const;
export type CertificateSubGroup = ValueOf<typeof certificateSubGroupValues>;

export const certificateSubGroupLabels: Record<CertificateSubGroup, string> = {
  LIFE: "Life",
  ADND: "AD&D",
  STD: "Short Term Disability",
  LTD: "Long Term Disability",
};

/**
 * A per-QPS-category-group employee certificate configuration. A client has one config per
 * category group present in their coverages, except BASIC and VOL which split into Life, AD&D,
 * STD, and LTD sub-groups (`certificateSubGroup`) that merge back into one QPS category on push.
 */
export type CertificateSplitConfig = {
  id: string;
  categoryCaseGroup: QPSSoldCaseCategoryGroupCode;
  certificateSubGroup: CertificateSubGroup | null;
  certificateSplitType: CertificateSplitType | null;
  certificateSplitBy: CertificateSplitBy[];
  certificateTitles: string[];
  createdAt: Date;
  updatedAt: Date;
  deletedAt: Date | null;
  createdBy: string;
  updatedBy: string;
  deletedBy: string | null;
  createdByUser: User;
  updatedByUser: User;
  deletedByUser: User | null;
};

export type CreateCertificateSplitConfigInput = Omit<
  CertificateSplitConfig,
  | "id"
  | "createdAt"
  | "updatedAt"
  | "deletedAt"
  | "createdBy"
  | "updatedBy"
  | "deletedBy"
  | "createdByUser"
  | "updatedByUser"
  | "deletedByUser"
>;

export const coverageChangesEffectiveAgeRules = [
  "IMMEDIATELY",
  "ANNUALLY_ON_POLICY_ANNIVERSARY",
] as const;
export type CoverageChangesEffectiveAgeRules = Extract<
  CoverageChangesEffective,
  "IMMEDIATELY" | "ANNUALLY_ON_POLICY_ANNIVERSARY"
>;

export const ageForDependentLifeBenefitsOptions = [
  "USE_THE_SPOUSE_AGE",
  "USE_THE_EMPLOYEE_AGE",
] as const;
export type AgeForDependentLifeBenefits = ValueOf<typeof ageForDependentLifeBenefitsOptions>;

export const approvedForAdvancedBillingValues = ["YES", "NO", "NOT_APPLICABLE"] as const;
export type ApprovedForAdvancedBilling = ValueOf<typeof approvedForAdvancedBillingValues>;

export const ageForDependentLifeBenefitsText: Record<AgeForDependentLifeBenefits, string> = {
  USE_THE_SPOUSE_AGE: "Use the spouse’s age",
  USE_THE_EMPLOYEE_AGE: "Use the employee’s age",
};

export type AdministerAEPOptions = "CALENDAR_MONTH_PRIOR_POLICY_ANNIVERSARY" | "CUSTOM";
export type AEPTakeEffectOptions = "ANNUALLY" | "OTHER";
export type DecreaseInsuranceTakeEffectOptions = "IMMEDIATELY" | "AT_ANNUAL_ENROLLMENT";
export type ChangesToBenefitsTakeEffectOptions = "IMMEDIATELY" | "FOFM" | "FOFM_INCLUSIVE";

export const disabilityClaimsPreferencesDefaults: DefaultsSelection[] = [
  "NO_SELECTION",
  "ACCEPTED_DEFAULTS",
  "EDITED_DEFAULTS",
];

export type CriticalIllnessValueAddedService =
  | "HEALTH_NAVIGATOR_HELPLINE"
  | "SELF_CARE_PLUS"
  | "NONE";

export const OnboardInstanceValues = ["Production", "Stage", "QA", "Local"] as const;
export type OnboardInstance = ValueOf<typeof OnboardInstanceValues>;

export const pfmlLeaveYearMethods = [
  "ROLLING_BACKWARD",
  "MEASURED_FORWARD",
  "CALENDAR_YEAR",
  "FIXED_YEAR",
] as const;
export type PFMLLeaveYearMethod = ValueOf<typeof pfmlLeaveYearMethods>;

export const pfmlLeaveYearFixedYearOption = ["EMPLOYEE_ANNIVERSARY", "CUSTOM_DATE"] as const;
export type PFMLLeaveYearFixedYearOption = ValueOf<typeof pfmlLeaveYearFixedYearOption>;

export const pfmlLeaveYearMethodLabelMap: Record<PFMLLeaveYearMethod, string> = {
  ROLLING_BACKWARD: "Rolling Backward",
  MEASURED_FORWARD: "Measured Forward",
  CALENDAR_YEAR: "Calendar Year",
  FIXED_YEAR: "Fixed Year",
};

export interface Client {
  id: ClientId;
  isActive: boolean;
  isTest: boolean;
  isBenefitsExplorer: boolean;
  isOnboard: boolean;
  isMaxwell: boolean;
  isArchived: boolean;
  name?: string | null;
  caseId?: string | null;
  ticketId: string;
  limitedEmails: boolean;
  baseClientId: string | null;
  implementationType: ImplementationType;
  inForceSLCoverages: SlfCoverageLongName[] | null;
  completedSetup: boolean;
  completedSetupAt: Date | null;
  /** @readonly Domain Calculated field  */
  allPoliciesSlfCoverages: SlfCoverageLongName[] | null;
  /** @readonly Domain Calculated field  */
  allPoliciesSlfCoveragesReady: SlfCoverageLongName[] | null;
  postOnboardingSurveySent: boolean;
  VDRReviewed: boolean | null;
  employeesNumber?: number | null;
  mvpBrokerTier?: MvpBrokerTier | null;
  eoiRequired?: boolean | null;
  sLConnectCredentialSentDate?: Date | null;
  baRegisteredSLConnect?: boolean | null;
  creCssEmailAddress?: string | null;
  creCssName?: string | null;
  territory?: string | null;
  priorCarrierCertificationsStatus?: PendingCompleteNotApplicable | null;
  NIGOReasons?: NIGOReason[] | null;
  releaseStatus: ReleaseStatus;
  jiraUpdatedAt?: Date | null;
  kickoffCallSelection?: YesNo | null;
  kickoffCallSchedule?: string | null;
  kickoffCallStatus: KickoffCallStatus | null;
  kickoffCallTopics?: string | null;
  kickoffCallPeople?: string | null;
  kickoffCallUpdatedAt?: Date | null;
  kickoffCallUpdatedBy: string | null;
  kickoffCallUpdatedByUser?: User | null;
  installCallSelection?: YesNo | null;
  installCallSchedule?: string | null;
  installCallTopics?: string | null;
  installCallPeople?: string | null;
  installCallUpdatedAt?: Date | null;
  installCallUpdatedBy: string | null;
  installCallUpdatedByUser?: User | null;
  enrollmentNeeds?: string | null;
  firstPolicyEffective?: Date | null;
  policyAnniversaryMonth: MonthOfTheYear | null;
  upcomingEnrollment?: YesNoNotSure | null;
  enrollmentDateStart?: Date | null;
  enrollmentDateEnd?: Date | null;
  dontKnowEnrollmentEventDates?: boolean | null;
  enrollmentNeedsUpdatedAt?: Date | null;
  enrollmentEventUpdatedAt?: Date | null;
  enrollmentNeedsUpdatedByUser?: User | null;
  enrollmentEventUpdatedBy: string | null;
  enrollmentEventUpdatedByUser?: User | null;
  alwaysShowBenefitSummaries: boolean;
  completedPaperworkReceivedOutsideOnboard: boolean | null;
  censusTaskHidden: boolean | null;
  benefitsExplorerCustomBenefitOrder: boolean;
  benefitsExplorerCustomBranding: boolean;
  nonSunLifeBenefitsExplorer: boolean;
  ageRules?: AgeRules[] | null;
  healthcareProfessionalsSegment: boolean | null;

  valueAddedServicesOptions: ValueAddedServicesOptions | null;
  previousValueAddedServicesOptions: ValueAddedServicesOptions | null;
  valueAddedServicesStepViewed: boolean | null;
  reasonForSelectingSunLife: ReasonForSelectingSunLife[] | null;
  reasonForSelectingSunLifeOther: string | null;
  benAdminUXImprovementsFeedback: string | null;
  setYourGoalsUpdatedAt?: Date | null;
  setYourGoalsUpdatedBy?: string | null;
  setYourGoalsUpdatedByUser: User | null;
  riskAcceptanceEmailSentDate: Date | null;
  genericDentalIdCardEmailSentDate: Date | null;
  personalizedDentalIdCardEmailSentDate: Date | null;
  eifBAReviewAndSubmitEmailSentDate: Date | null;
  slfImplementationConsultant: Omit<SimplifiedDomainUser, "clientIds" | "role"> | null;
  slfImplementationConsultantId: string | null;
  blockOfBusiness: BlockOfBusiness | null;

  ebrEmailAddress?: string | null;
  issuedStateSitus: LocationStateCode[] | null; // @todo: remove after multi policies implementation
  users?: DomainClientUser[] | null;
  tasks?: DomainTask[] | null;
  policies: Policy[];
  liveReleaseStatusAt: Date | null;

  clientErrors: ClientError[] | null;

  limitDueDateEmails: boolean;

  bills: Bill[];
  deletedBills: Bill[];
  plans: Plan[];
  contacts: Contact[];
  subsidiaries: Subsidiary[];
  apiJobs: APIJob[];

  /**
   * EIF Related
   **/
  digitalEIF?: boolean | null;
  prefillCompanyInformation: PrefillCompanyInformation;
  needsEmployeeClasses?: YesNo | null;

  taxId: string | null;
  previouslyInsuredBySunLife?: YesNo | null;
  previousSlfPolicies: string[] | null;
  allowClientSelfServicePlanConfig: YesNo;
  allowClientToViewPlanConfig: boolean | null;
  benefitsReplacedWithSlf: SlfCoverageLongName[] | "none" | null; // @todo: remove when benefits are defined by client.
  authorizedSignerUserId: string | null;
  authorizedSignerUser?: DomainUser | null;
  brokerAsAuthorizedSigner: boolean | null;
  outsideSigner: OutsideSigner | null;
  eifSignedAt: Date | null;
  eifSignedBy: UserId | null;
  eifSignedByUser: User | null;
  eifOutsideSignedBy: OutsideSignerId | null;
  eifOutsideSignedByUser: OutsideSigner | null;
  pfmlPreferencesStepViewed: boolean | null;
  fliPreferencesStepViewed: boolean | null;
  fliCoversAllEmployees: boolean | null;
  fliExcludedGroups: string[] | null;
  pfmlLeaveYearMethod: PFMLLeaveYearMethod | null;
  pfmlLeaveYearFixedYearOption: PFMLLeaveYearFixedYearOption | null;
  pfmlFixedYearMMDD: string | null;

  disabilityClaimsPreferencesDefaults: DefaultsSelection;
  claimsCheckPreferencesDefaults: DefaultsSelection;
  claimsCheckStepViewed: boolean | null;

  monthlyClaimsReportsAndEOBsDefaults: DefaultsSelection;
  monthlyClaimsReportMailingLocations: MonthlyClaimsReportMailingLocation[] | null;

  // Additional waiting period rules
  unionMembersCovered: YesNo | null;
  domesticPartnersCovered: YesNo | null;
  timeSpentShouldCountTowardsWaitingPeriod: YesNo | null;
  timeSpentAsPartTimeShouldCount: boolean | null;
  timeSpentAsOtherShouldCount: boolean | null;
  timeSpentAsOtherShouldCountText: string | null;
  waitingPeriodShouldBeWaived: YesNo | null;

  // Employee Certificates
  shouldCertificatesBeSplitInAnotherWay: YesNo | null;
  shouldCertificatesBeSplitInAnotherWayText: string | null;
  certificateSplitConfigs: CertificateSplitConfig[];

  // EIF - rehire provisions
  reHireProvisionExists: YesNo | null;
  reHireProvisionUnit: RehireProvisionUnit | null;
  reHireProvisionDuration: number | null;
  rehireProvision: RehireProvision | null;

  // Section 125
  hasSection125Plan: YesNo | null;
  hasSection125Dental: boolean | null;
  hasSection125Vision: boolean | null;
  hasSection125DHMO: boolean | null;
  hasAdditionalCoverageIncludedIn125Plan: boolean | null;
  additonalCoverageIncludedInSection125Plan: CoverageNotIncludedInSection125Plan[] | null;

  // Age Rules
  ageReductionDecreaseTakeEffectWhen: CoverageChangesEffectiveAgeRules | null;
  ageBandIncreaseTakeEffectWhen: CoverageChangesEffectiveAgeRules | null;
  ageForDependentLifeBenefits: AgeForDependentLifeBenefits | null;
  changesForDependentLifeBenefitsUseAge: YesNo | null;

  // Other Changes to Coverage
  otherCoverageChangesTakeEffectWhen: CoverageChangesEffective | null;
  firstOfTheFollowingMonthIncludesFirstDay: boolean | null;

  // Changes during annual enrollment
  whenDoYouAdministerYourAEP: AdministerAEPOptions | null;
  whenDoYouAdministerYourAEPStartDate: string | null;
  whenDoYouAdministerYourAEPEndDate: string | null;
  whenDoAEPTakeEffect: AEPTakeEffectOptions | null;
  whenDoAEPTakeEffectOther: string | null;
  whenDecreasesInsuranceTakeEffect: DecreaseInsuranceTakeEffectOptions | null;
  changesOnlyAllowedDuringAE: YesNo | null;
  whenDoChangesToBenefitsTakeEffect: ChangesToBenefitsTakeEffectOptions | null;

  // Web Admin
  /**
   * @deprecated No longer identifying contact type via id on the Client. Rather we are idenfitying contacts by their contact type
   */
  webAdminContactId?: string | null;
  webAdminContact?: Contact | null;
  additionalWebAdminContacts?: Contact[];

  // ERISA
  erisaHasPlan: YesNo | null;
  erisaPlanType: ERISAPlanType | null;
  erisaPlanTypeOther: string | null;
  erisaPrefersPlanDetails: YesNo | null;
  erisaPlanAdminName: string | null;
  erisaStreetAddress: string | null;
  erisaCity: string | null;
  erisaState: LocationStateCode | null;
  erisaZipCode: string | null;
  erisaPlanNumber: ERISAPlanNumber | null;
  erisaPlanNumberOther: string | null;
  erisaPlanYearEndDate_deprecated: string | null;
  erisaPlanYearEndDateMMDD: string | null;
  erisaAdministerOwnPlan: YesNo | null;
  erisaHasAgentForLegalProcess: YesNo | null;
  erisaIsLegalAgentSameAsAdmin: boolean | null;
  erisaLegalAgentName: string | null;
  erisaLegalAgentStreetAddress: string | null;
  erisaLegalAgentCity: string | null;
  erisaLegalAgentState: LocationStateCode | null;
  erisaLegalAgentZipCode: string | null;

  // Subsidiaries and Affiliate
  hasSubsidiary: YesNo | null;
  subsidiaryEEsInNYNJOrHI: YesNo | null;
  mainPolicyHolderEEsInNYNJOrHI: YesNo | null;
  mainPolicyHolderEEStates: DisabilityStateCode[] | null;

  deifChangesAcceptedAt: Date | null;
  deifChangesAcceptedBy: string | null;
  deifChangesAcceptedByUser?: User | null;
  deifChangesOutsideAcceptedBy: OutsideSignerId | null;
  deifChangesOutsideAcceptedByUser: OutsideSigner | null;
  deifChangesDeclinedAt: Date | null;
  deifChangesDeclinedBy: string | null;
  deifChangesDeclinedByUser?: User | null;
  deifChangesOutsideDeclinedBy: OutsideSignerId | null;
  deifChangesOutsideDeclinedByUser: OutsideSigner | null;
  deifChangesDeclinedReason: string | null;
  deifChangesReadyForReviewAt: Date | null;
  deifChangesReadyForReviewBy: string | null;
  deifChangesReadyForReviewByUser?: User | null;
  deifChangesUndoneAt: Date | null;
  deifChangesUndoneBy: string | null;

  allEmployeesActivelyAtWork: YesNo | null;
  allBenefitsNonContributory: YesNo | null;
  activelyAtWorkUpdatedAt: Date | null;
  activelyAtWorkUpdatedBy: string | null;

  malpracticeReimbursementRider: YesNo | null;
  malpracticeReimbursementPayableTo: BenefitPayableTo | null;

  criticalIllnessType: IssueOrAttainedAge | null;
  criticalIllnessRateType: Tobacco | null;

  criticalIllnessDependentAge: EmployeeSpouse | null;
  criticalIllnessDependentTobacco: EmployeeSpouse | null;
  criticalIllnessValueAddedService: CriticalIllnessValueAddedService | null;

  deductibleMaximumReportTask: PendingCompleteNotApplicable | null;

  createdAt: Date;
  createdBy: string | null;
  updatedAt: Date;
  updatedBy: string | null;
  deletedAt?: Date | null;
  deletedBy: string | null;
}
export const clientSetupStepID = [
  "MERGE_CLIENT_INSTANCES",
  "ADD_BROKER",
  "ADD_BEN_ADMIN",
  "ADD_CENSUS_TEMPLATE",
  "ADD_DOCUSIGN",
  "CONFIGURE_COMPANY",
  "PREFILL_COMPANY",
  "ADD_RESOURCES",
  "UPDATE_JIRA",
  "SEND_WELCOME_EMAIL",
  "CONFIRM_SETUP",
  "IMPORT_COMPANY_INFORMATION",
] as const;

export type ClientSetupStepId = ValueOf<typeof clientSetupStepID>;

export const clientSetupStepStatus = ["VIEWED", "COMPLETED"] as const;

export type ClientSetupStepStatus = ValueOf<typeof clientSetupStepStatus>;

export type ClientInput<ContactType = ContactInput> = Omit<Client, "webAdminContact"> & {
  webAdminContact?: ContactType | null;
};

export const EAPIsBlendedWithValues = ["Not Applicable", "LIFE", "STD", "LTD", "PEPM"] as const;
export type EAPIsBlendedWith = ValueOf<typeof EAPIsBlendedWithValues>;

export interface Policy {
  id: PolicyId;
  clientId: ClientId;
  ticketId: string;
  policyEffective: Date | null;
  initialPolicyEffectiveDate: Date | null;
  /** @readonly Domain Calculated field  */
  daysToPolicyEffective: number | null;
  ticketStatus: TicketStatus | null;
  primaryPolicy: boolean;
  phaseId: PhaseId | null;
  phaseChangeToInfoGatheringSent: boolean;
  phaseChangeToReviewAndValidateSent: boolean;
  phaseChangeToPlanApprovalSent: boolean;
  phaseChangeToContractsAndBillingSent: boolean;
  phaseChangeToLiveSent: boolean;
  riskAcceptanceEmailSent: boolean;
  dentalRolloverUpdatedEmailSent: boolean;
  slfCoverages: SlfCoverageLongName[] | null;
  slfCoveragesReady: SlfCoverageLongName[] | null;
  slfPolicyNumber: string | null;
  enrollmentCensusDeadline: Date | null;
  infoGatheringDeadline: Date | null;
  riskAcceptanceEmailSentDate: Date | null;
  genericDentalIdCardEmailSentDate: Date | null;
  personalizedDentalIdCardEmailSentDate: Date | null;
  liveStatusDate: Date | null;
  frontloading: Frontloading | null;
  priorCarrierBill: PendingCompleteNotApplicable | null;
  spdCompletedForPFML: boolean;
  sunAdvisorSPDComplete: boolean | null;
  sunAdvisorAdvicePlusPreFundingAmount: number | null;
  issuedStateSitus: LocationStateCode[] | null;
  approvedPFMLPlans: boolean | null;
  connecticutUseSLFSPDForPFML: boolean | null;
  coloradoUseSLFSPDForPFML: boolean | null;
  massachusettsUseSLFSPDForPFML: boolean | null;
  oregonUseSLFSPDForPFML: boolean | null;
  washingtonUseSLFSPDForPFML: boolean | null;
  massachusettsPFMLCheckCutting: number | null;
  connecticutPFMLCheckCutting: number | null;
  washingtonPFMLCheckCutting: number | null;
  coloradoPFMLCheckCutting: number | null;
  oregonPFMLCheckCutting: number | null;
  mainePFMLCheckCutting: number | null;
  delawarePFMLCheckCutting: number | null;
  minnesotaPFMLCheckCutting: number | null;
  dentalRollover: DentalRollover | null;
  dentalRolloverCompleted: boolean | null;
  noDentalRollover: boolean | null;
  approvedForAdvancedBilling: ApprovedForAdvancedBilling | null;
  pushedToCompassAt: Date | null;
  policyTasks: DomainPolicyTask[] | null;
  qpsEvents: QPSEvent[];
  eapIsBlendedWith: EAPIsBlendedWith | null;
  onboardInstance: OnboardInstance | null;
  adminPlatformIsTransmissionSystem: YesNoNotSure | null;
  dontKnowProdSupportContactInfo: boolean | null;
  dontKnowDataFeedContactInfo: boolean | null;
  dontKnowApiConnectionContactInfo: boolean | null;
  apiConnectionDeclined: boolean | null;
  apiConnectionTypes: BenAdminAndDataFeedApiConnection[] | null;
  dataFeedsTransmissionPlatform: BenAdminPlatform | null;
  dataFeedsTransmissionPlatformId: number | null;
  dataFeedsTransmissionPlatformOtherName: string | null;
  dontKnowApiProdSupportContactInfo: boolean | null;
  salesforceConnectorNumber: string | null;

  /**
   * DEIF
   */

  // Company Details - Benefits administration & data feeds
  hasBenAdminPlatform: YesNoNotSure | null;
  benAdminPlatformId: number | null;
  benAdminPlatform: BenAdminPlatform | null;
  benAdminPlatformOtherName: string | null;
  benAdminPlatformUpdatedAt: Date | null;
  benAdminPlatformUpdatedBy: string | null;
  benAdminPlatformUpdatedByUser: User | null;
  dataFeeds: YesNoNotSure | null;
  dataFeedsUpdatedAt: Date | null;
  dataFeedsUpdatedBy: string | null;
  dataFeedsUpdatedByUser: User | null;
  dataFeedsImplementorContact: Contact | null;
  dataFeedsProductionSupportContact: Contact | null;
  dataFeedsBenAdminContact: Contact | null;
  dataFeedsApiConnectionContact: Contact | null;
  dataFeedsApiProdSupportContact: Contact | null;
  dataFeedsAdditionalContacts: Contact[] | null;

  // Plan Administrators & Billing - Billing Preferences
  billingAdministrationType: BillingAdministrationType | null;
  billingStructureType: BillingStructureType | null;
  billingSummaryStatementType: BillingSummaryStatementType | null;
  billPayrollCycles: BillPayrollCycle[] | null;
  billPayrollCyclesOther: string | null;
  billPayrollCyclesExplanation: string | null;
  billSplitType: BillSplitType | null;
  whoSubmitsClaims: WhoSubmitsClaims | null;
  tpaContact: Contact | null;
  /**
   * @deprecated
   * We don't care about this type because we never read from this value ever.
   * The only reason we have it is to write to it, and by writing to it it gets
   * tracked in our change log tables and we can use that to show IC Edit stamps
   * in the "Advance or Arrears" radio buttons in the Billing Preferences step.
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any -- we don't care about this type
  advanceOrArrears: any;

  jiraUpdatedAt: Date;
  jiraCreatedAt: Date;
  createdAt: Date;
  createdBy: string | null;
  updatedAt: Date;
  updatedBy: string | null;
  deletedAt?: Date | null;
  deletedBy: string | null;
}

export type PolicyInput<ContactInputType = ContactInput> = {
  noDentalRollover?: boolean;
  dentalRolloverUpdatedEmailSent?: boolean;

  // Company Details - Benefits administration & data feeds
  hasBenAdminPlatform: YesNoNotSure | null;
  benAdminPlatformId: number | null;
  benAdminPlatform: BenAdminPlatform | null;
  benAdminPlatformOtherName: string | null;
  dataFeeds: YesNoNotSure | null;
  dataFeedsUpdatedAt: Date | null;
  dataFeedsImplementorContact: ContactInputType | null;
  dataFeedsProductionSupportContact: ContactInputType | null;
  dataFeedsBenAdminContact: ContactInputType | null;
  dataFeedsApiConnectionContact: ContactInputType | null;
  dataFeedsApiProdSupportContact: ContactInputType | null;
  dataFeedsAdditionalContacts: ContactInputType[] | null;
  adminPlatformIsTransmissionSystem: YesNoNotSure | null;
  dontKnowProdSupportContactInfo: boolean | null;
  dontKnowDataFeedContactInfo: boolean | null;
  dontKnowApiConnectionContactInfo: boolean | null;
  dontKnowApiProdSupportContactInfo: boolean | null;
  apiConnectionDeclined: boolean | null;
  apiConnectionTypes: BenAdminAndDataFeedApiConnection[] | null;
  dataFeedsTransmissionPlatform: BenAdminPlatform | null;
  dataFeedsTransmissionPlatformId: number | null;
  dataFeedsTransmissionPlatformOtherName: string | null;

  // Plan Administrators & Billing - Billing Preferences
  billingAdministrationType: BillingAdministrationType | null;
  billingStructureType: BillingStructureType | null;
  billingSummaryStatementType: BillingSummaryStatementType | null;
  billPayrollCycles: BillPayrollCycle[] | null;
  billPayrollCyclesOther: string | null;
  billPayrollCyclesExplanation: string | null;
  billSplitType: BillSplitType | null;
  whoSubmitsClaims: WhoSubmitsClaims | null;
  tpaContact: ContactInputType | null;
  advanceOrArrears: unknown;

  connecticutUseSLFSPDForPFML?: boolean;
  massachusettsUseSLFSPDForPFML?: boolean;
  washingtonUseSLFSPDForPFML?: boolean;
  coloradoUseSLFSPDForPFML?: boolean;
  oregonUseSLFSPDForPFML?: boolean;
};
export type ClientSetupStatus =
  | "Not Started"
  | "In Progress"
  | "In Review"
  | "Needs Review"
  | "Done";

export type ClientSetup = {
  mergeClient: {
    status: ClientSetupStatus;
  };
  broker: {
    data: Broker[];
    status: ClientSetupStatus;
  };
  benAdmin: {
    data: BenAdmin[];
    status: ClientSetupStatus;
  };
  censusTemplate: {
    status: ClientSetupStatus;
  };
  docusignEnvelop: {
    data: readonly OnboardingFormWithMetadata[];
    status: ClientSetupStatus;
  };
  configCompanyInfo: {
    status: ClientSetupStatus;
  };
  importCompanyInfo: {
    status: ClientSetupStatus;
    markedAsCompleteAt: Date | null;
    updatedAt: Date | null;
    updatedByFullName: string | null;
  };
  prefillCompanyInfo: {
    status: ClientSetupStatus;
  };
  enrollmentResources: {
    status: ClientSetupStatus;
  };
  updateJiraTicket: {
    status: ClientSetupStatus;
  };
  welcomeEmail: {
    status: ClientSetupStatus;
  };
  completeSetup: {
    status: ClientSetupStatus;
  };
};

export type ClientSetupSteps = {
  clientId: ClientId;
  stepId: ClientSetupStepId;
  stepStatus: ClientSetupStepStatus;
  markedAsCompleteAt: Date | null;
  updatedAt: Date;
  updatedByUser: {
    id: UserId;
    email: string;
    firstName: string;
    lastName: string;
    fullName: string;
  } | null;
};

export type ClientTableQuery = {
  limit?: number;
  offset?: number;
  sortBy?:
    | "caseId"
    | "firstPolicyEffective"
    | "createdAt"
    | "name"
    | "slfImplementationConsultant"
    | "policies"
    | "implementationType";
  sortDirection?: "asc" | "desc";
} & ClientTableFilters;

export type ClientTableFilters = {
  search?: string;
  userId?: string;
  ic?: string[];
  noic?: boolean;
  completedSetup?: boolean;
  isOnboard?: boolean;
  isBenefitsExplorer?: boolean;
  isNewIssue?: boolean;
  isLiveClient?: boolean;
  isRiskRejected?: boolean;
  isAddCoverage?: boolean;
  phaseIds?: PhaseId[];
  ebrEmails?: string[];
  includeArchived?: boolean;
  clientErrorTypes?: ClientErrorType[];
};

export const isClientTableFilters = (
  filters: Record<string, unknown>,
): filters is ClientTableFilters => {
  const props: Record<keyof ClientTableFilters, null> = {
    search: null,
    userId: null,
    ic: null,
    noic: null,
    completedSetup: null,
    isOnboard: null,
    isBenefitsExplorer: null,
    isNewIssue: null,
    isAddCoverage: null,
    isLiveClient: null,
    isRiskRejected: null,
    phaseIds: null,
    ebrEmails: null,
    includeArchived: null,
    clientErrorTypes: null,
  };

  const isIt = Object.keys(props).every((prop) => filters[prop] == null || prop in filters);

  return isIt;
};

export type ClientUserTableFilters = ClientTableFilters & {
  userId: string;
  phaseIds?: PhaseId[];
  completedSetup?: boolean;
};

export type ClientPolicy = {
  ticketId: string;
  ticketStatus: TicketStatus | null;
  primaryPolicy: boolean;
  priorCarrierBill: PendingCompleteNotApplicable | null;
  enrollmentCensusDeadline: Date | null;
  infoGatheringDeadline: Date | null;
  slfCoverages: SlfCoverageLongName[];
  slfCoveragesReady: SlfCoverageLongName[];
  slfPolicyNumber: string[];
  policyEffective: Date | null;
  frontloading: Frontloading | null;
  approvedPFMLPlans: boolean;
  sunAdvisorSPDComplete: boolean;
  spdCompletedForPFML: boolean;
  sunAdvisorAdvicePlusPreFundingAmount: number | null;
  issuedStateSitus: LocationStateCode[];
  massachusettsPFMLCheckCutting: number | null;
  connecticutPFMLCheckCutting: number | null;
  washingtonPFMLCheckCutting: number | null;
  coloradoPFMLCheckCutting: number | null;
  oregonPFMLCheckCutting: number | null;
  jiraUpdatedAt: Date;
  jiraCreatedAt: Date;
  phaseId: PhaseId | null;
};

export const NIGOReasons = [
  "Awaiting Census",
  "Census Missing Information",
  "Paperwork Missing Information",
  "Missing Prior Carrier Information",
  "Pending Risk Rejection",
  "BA Signature Missing",
  "Broker Signature Missing",
  "Prior Carrier Cert Missing",
  "Prior Carrier Bill Missing",
  "Pending Broker Licensing",
  "Pending MDT Formatting",
  "Pending Prior Carrier Review Confirmations",
  "Pending State Approval",
  "Prescreen Review",
];
export type NIGOReason = ValueOf<typeof NIGOReasons>;

export const ageRules = ["Age Reductions", "Age Band Rates"] as const;
export type AgeRules = ValueOf<typeof ageRules>;

export const valueAddedServicesOptions = [
  "ONLINE_WILL_PREPARATION",
  "EMERGENCY_TRAVEL_ASSISTANCE",
  "SELF_CARE_PLUS",
  "WELLTHY",
] as const;
export type ValueAddedServicesOptions = ValueOf<typeof valueAddedServicesOptions>;

export const valueAddedServicesOptionsVTWellthy = ["EMERGENCY_TRAVEL_ASSISTANCE"] as const;
export type ValueAddedServicesOptionsVT = Extract<
  ValueAddedServicesOptions,
  "ONLINE_WILL_PREPARATION" | "EMERGENCY_TRAVEL_ASSISTANCE"
>;

export const valueAddedServicesOptionsText: Record<ValueAddedServicesOptions, string> = {
  ONLINE_WILL_PREPARATION: "Online Will Preparation and Claimant Support Services",
  EMERGENCY_TRAVEL_ASSISTANCE: "Emergency Travel Assistance and Identity Theft Protection",
  SELF_CARE_PLUS: "Self Care+",
  WELLTHY: "Wellthy",
};

export function getIsNIGOReason(reason: string): reason is NIGOReason {
  const isNIGOReason = NIGOReasons.includes(reason);
  return isNIGOReason;
}

export function getIsBlockOfBusiness(value: string): value is BlockOfBusiness {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const isBlockOfBusiness = blockOfBusinessValues.includes(value as BlockOfBusiness);
  return isBlockOfBusiness;
}

export type MultiPolicyTicket = PrimaryTicket & {
  firstPolicyEffective: Date | null;
  isActive: boolean;
  /*
   * This differs from `liveStatusDate` because `liveReleaseStatusAt`
   * represents the date at which all the linked policies became live
   */
  liveReleaseStatusAt: Date | null;
  releaseStatus: ReleaseStatus;
  policies: PolicyTicket[];
};

export interface ClientBaseInfoResponse {
  name: string;
  caseId: string;
  policyEffective: (string | null)[];
}

const pfmlKeys = [
  "ASO CT PFML",
  "CT PFML Fully Ins",
  "ASO MA PFML",
  "MA PFML Fully Ins",
  "ASO WA PFML",
  "ASO CO PFML",
  "CO PFML Fully Ins",
  "ASO OR PFML",
  "OR PFML Fully Ins",
] as const;
export type PFMLCoverageTypes = ValueOf<
  Pick<typeof slfCoverageNameToLongName, ValueOf<typeof pfmlKeys>>
>;
export const PFMLCoverages: PFMLCoverageTypes[] = pfmlKeys.map(
  (key) => slfCoverageNameToLongName[key],
);

const pfmlSpdKeys = [
  "ASO CT PFML",
  "ASO MA PFML",
  "ASO WA PFML",
  "ASO CO PFML",
  "ASO OR PFML",
  "ASO DE PFML",
  "ASO ME PFML",
  "ASO MN PFML",
] as const;
type PFMLSPDCoverageTypes = ValueOf<
  Pick<typeof slfCoverageNameToLongName, ValueOf<typeof pfmlSpdKeys>>
>;
export const PFMLSPDCoverages: PFMLSPDCoverageTypes[] = pfmlSpdKeys.map(
  (key) => slfCoverageNameToLongName[key],
);

const sunAdvisorKeys = ["Sun Advisor Advice to Pay", "SunAdvisor Advice Plus"] as const;
type SunAdvisorCoverageTypes = ValueOf<
  Pick<typeof slfCoverageNameToLongName, ValueOf<typeof sunAdvisorKeys>>
>;
export const SunAdvisorCoverages: SunAdvisorCoverageTypes[] = sunAdvisorKeys.map(
  (key) => slfCoverageNameToLongName[key],
);

export const kickoffCallStatus = [
  "Pending",
  "Complete",
  "Complete - No Recording",
  "Complete - Call Recorded",
  "Declined",
  "No Response",
];
export type KickoffCallStatus = ValueOf<typeof kickoffCallStatus>;

export const censusStepIdMap = {
  "upload-files": "Upload your file",
  "indicate-source": "Confirm your file format",
  "validate-submit": "Review validation results",
} as const;
export type CensusStepId = keyof typeof censusStepIdMap;

export const billingAdministrationTypes: BillingAdministrationType[] = ["LIST", "SELF", "TPA"];
export const billingAdministrationTypeLabels: { [key in BillingAdministrationType]: string } = {
  LIST: "List bill",
  SELF: "Self-bill",
  TPA: "TPA administration",
};

export const billingStructureTypes: BillingStructureType[] = ["SINGLE", "MULTIPLE"];
export const billingStructureTypeLabels: { [key in BillingStructureType]: string } = {
  SINGLE: "Single bill",
  MULTIPLE: "Multiple bills",
};
export const alternateBillingStructureTypeLabels: { [key in BillingStructureType]: string } = {
  SINGLE: "One summary statement and payment",
  MULTIPLE: "Multiple statements and payments",
};

export const whoSubmitsClaimsLabels: { [key in WhoSubmitsClaims]: string } = {
  PRIMARY_PLAN_ADMINISTRATOR: "The plan administrator",
  THIRD_PARTY_ADMINISTRATOR: "The third party administrator",
};

export const billingSummaryStatementTypes: BillingSummaryStatementType[] = ["SUN_LIFE", "CUSTOM"];

// Remove LOCATION here when ONBOARD_SIMPLIFY_BILLING_PREFERENCES is released
export const billSplitTypes: BillSplitType[] = ["LOCATION", "BENEFIT", "TAGS", "NONE"];

export const billPayrollCycles = ["WEEKLY", "BIWEEKLY", "SEMIMONTHLY", "MONTHLY", "OTHER"] as const;
export type BillPayrollCycle = ValueOf<typeof billPayrollCycles>;
export const billingPayrollCycleTypeLabels: Record<BillPayrollCycle, string> = {
  WEEKLY: "Weekly",
  BIWEEKLY: "Bi-weekly",
  SEMIMONTHLY: "Semi-monthly",
  MONTHLY: "Monthly",
  OTHER: "Other",
};

export function getIsBillPayrollCycle(item: string): item is BillPayrollCycle {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const isIt = billPayrollCycles.includes(item as BillPayrollCycle);
  return isIt;
}

export const pfmlSlfCoverages: PFMLCoverage[] = [
  "CT PFML Fully Ins",
  "MA PFML Fully Ins",
  "CO PFML Fully Ins",
  "OR PFML Fully Ins",
  "DE PFML Fully Ins",
  "ME PFML Fully Ins",
  "MN PFML Fully Ins",
  "ASO CT PFML",
  "ASO MA PFML",
  "ASO WA PFML",
  "ASO CO PFML",
  "ASO OR PFML",
  "ASO DE PFML",
  "ASO ME PFML",
  "ASO MN PFML",
];

export const getIsAgeRule = (item: string): item is AgeRules => {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const isIt = ageRules.includes(item as AgeRules);
  return isIt;
};

export type RehireProvisionMonths = Extract<
  RehireProvision,
  | "MONTHS_1"
  | "MONTHS_2"
  | "MONTHS_3"
  | "MONTHS_4"
  | "MONTHS_6"
  | "MONTHS_9"
  | "MONTHS_12"
  | "MONTHS_18"
  | "MONTHS_24"
  | "MONTHS_36"
  | "MONTHS_60"
>;
export const rehireProvisionMonths: readonly RehireProvisionMonths[] = [
  "MONTHS_1",
  "MONTHS_2",
  "MONTHS_3",
  "MONTHS_4",
  "MONTHS_6",
  "MONTHS_9",
  "MONTHS_12",
  "MONTHS_18",
  "MONTHS_24",
  "MONTHS_36",
  "MONTHS_60",
] as const;

export type RehireProvisionDays = Extract<
  RehireProvision,
  "DAYS_30" | "DAYS_45" | "DAYS_60" | "DAYS_90" | "DAYS_120"
>;
export const rehireProvisionDays = [
  "DAYS_30",
  "DAYS_45",
  "DAYS_60",
  "DAYS_90",
  "DAYS_120",
] as const;

export const rehireProvisions: readonly RehireProvision[] = [
  "MONTHS_1",
  "MONTHS_2",
  "MONTHS_3",
  "MONTHS_4",
  "MONTHS_6",
  "MONTHS_9",
  "MONTHS_12",
  "MONTHS_18",
  "MONTHS_24",
  "MONTHS_36",
  "MONTHS_60",
  "DAYS_30",
  "DAYS_45",
  "DAYS_60",
  "DAYS_90",
  "DAYS_120",
] as const;

export const rehireProvisionLabels = [
  "1 month",
  "2 months",
  "3 months",
  "4 months",
  "6 months",
  "9 months",
  "12 months",
  "18 months",
  "24 months",
  "36 months",
  "60 months",
  "30 days",
  "45 days",
  "60 days",
  "90 days",
  "120 days",
] as const;
export type RehireProvisionLabels = ValueOf<typeof rehireProvisionLabels>;

export const rehireProvisionValueToLabelMap: Readonly<
  Record<RehireProvision, RehireProvisionLabels>
> = Object.freeze({
  MONTHS_1: "1 month",
  MONTHS_2: "2 months",
  MONTHS_3: "3 months",
  MONTHS_4: "4 months",
  MONTHS_6: "6 months",
  MONTHS_9: "9 months",
  MONTHS_12: "12 months",
  MONTHS_18: "18 months",
  MONTHS_24: "24 months",
  MONTHS_36: "36 months",
  MONTHS_60: "60 months",
  DAYS_30: "30 days",
  DAYS_45: "45 days",
  DAYS_60: "60 days",
  DAYS_90: "90 days",
  DAYS_120: "120 days",
});

export const monthsOfTheYear = [
  "JANUARY",
  "FEBRUARY",
  "MARCH",
  "APRIL",
  "MAY",
  "JUNE",
  "JULY",
  "AUGUST",
  "SEPTEMBER",
  "OCTOBER",
  "NOVEMBER",
  "DECEMBER",
] as const;

export type MonthOfTheYear = ValueOf<typeof monthsOfTheYear>;

export const prefillCompanyInformation = ["Not Started", "In Progress", "Done"] as const;
export type PrefillCompanyInformation = ValueOf<typeof prefillCompanyInformation>;

// All clients created after this date won't have the Accident and Travel Assist value-added service available
export const accidentTravelAssistDateLimit = new Date("2026-08-01");

export type AdvanceOrArrears = Partial<Record<SlfCoverageLongName, "Arrears" | "Advance">>;
export const isAdvanceOrArrearsRecord = (value: unknown): value is AdvanceOrArrears => {
  return (
    value != null &&
    typeof value === "object" &&
    Object.keys(value).every(getIsSlfCoverageLongName) &&
    Object.values(value).every((v) => v === "Arrears" || v === "Advance")
  );
};

export type ImportedBillingContact = Yup.InferType<typeof importedBillingContactValidation>;
export type ImportedBillingPreferences = Yup.InferType<typeof importedBillingPreferencesValidation>;

export type ImportedClientData = {
  "benefits-administration-and-data-feed":
    | (Pick<Policy, "hasBenAdminPlatform" | "benAdminPlatformOtherName"> & {
        benAdminPlatform: Pick<BenAdminPlatform, "id" | "name"> | null;
      })
    | null;

  "tax-id": Pick<Client, "taxId"> | null;

  "current-sun-life-policies": Pick<
    Client,
    "previouslyInsuredBySunLife" | "previousSlfPolicies"
  > | null;

  "plan-administrators": Array<
    Pick<
      ContactInput,
      "type" | "firstName" | "lastName" | "email" | "phoneNumber" | "faxNumber" | "title"
    > & {
      location: Pick<Location, "address1" | "address2" | "city" | "state" | "zipCode"> | null;
    }
  >;

  "billing-preferences": ImportedBillingPreferences[] | null;

  "additional-waiting-period-rules": Array<{
    policyNumber: number;
    data: Partial<
      Pick<Client, "timeSpentShouldCountTowardsWaitingPeriod" | "timeSpentAsPartTimeShouldCount">
    >;
  }>;

  "rehire-provision": Array<{
    policyNumber: number;
    data: Pick<Client, "reHireProvisionExists" | "rehireProvision">;
  }>;

  "union-members-and-domestic-partners": Array<{
    data: Pick<Client, "unionMembersCovered" | "domesticPartnersCovered">;
  }>;

  erisa: Pick<
    Client,
    | "erisaHasPlan"
    | "erisaPrefersPlanDetails"
    | "erisaPlanNumber"
    | "erisaPlanNumberOther"
    | "erisaPlanYearEndDateMMDD"
    | "taxId"
    | "erisaPlanType"
    | "erisaPlanTypeOther"
    | "erisaAdministerOwnPlan"
    | "erisaPlanAdminName"
    | "erisaStreetAddress"
    | "erisaCity"
    | "erisaState"
    | "erisaZipCode"
    | "erisaHasAgentForLegalProcess"
    | "erisaIsLegalAgentSameAsAdmin"
    | "erisaLegalAgentName"
    | "erisaLegalAgentStreetAddress"
    | "erisaLegalAgentCity"
    | "erisaLegalAgentState"
    | "erisaLegalAgentZipCode"
  > | null;

  "value-added-services": Array<{
    policyNumber: number;
    data: Pick<
      Client,
      | "valueAddedServicesStepViewed"
      | "valueAddedServicesOptions"
      | "previousValueAddedServicesOptions"
    >;
  }>;
};

export type ImportedClientDataSections = keyof ImportedClientData;

export const importedClientDataSections: ImportedClientDataSections[] = [
  "benefits-administration-and-data-feed",
  "tax-id",
  "current-sun-life-policies",
  "plan-administrators",
  "billing-preferences",
  "additional-waiting-period-rules",
  "rehire-provision",
  "union-members-and-domestic-partners",
  "erisa",
  "value-added-services",
];

export type ImportedClientDataRequest = {
  "benefits-administration-and-data-feed"?: Partial<
    ImportedClientData["benefits-administration-and-data-feed"]
  >;
  "tax-id"?: Partial<ImportedClientData["tax-id"]>;
  "current-sun-life-policies"?: Partial<ImportedClientData["current-sun-life-policies"]>;
  "plan-administrators"?: Array<ImportedClientData["plan-administrators"][0]>;
  "billing-preferences"?: ImportedClientData["billing-preferences"];
  "additional-waiting-period-rules"?: Partial<
    ImportedClientData["additional-waiting-period-rules"][0]
  >;
  "rehire-provision"?: Partial<ImportedClientData["rehire-provision"][0]>;
  "union-members-and-domestic-partners"?: Partial<
    ImportedClientData["union-members-and-domestic-partners"][0]
  >;
  erisa?: Partial<ImportedClientData["erisa"]>;
  "value-added-services"?: Partial<ImportedClientData["value-added-services"]>[0];
};

export const clientErrorSorter = (a: ClientError, b: ClientError) => {
  const order: ClientErrorType[] = [
    "SUBMIT_COMPANY_INFORMATION_SETUP_INCOMPLETE",
    "SUBMIT_COMPANY_INFORMATION_INCOMPLETE",
    "UNSENT_IC_EDITS",
  ];

  // sort by error type first if error type matters for order
  if (order.includes(a.errorType) && order.includes(b.errorType))
    return order.indexOf(a.errorType) - order.indexOf(b.errorType);
  if (order.includes(a.errorType)) return -1;
  if (order.includes(b.errorType)) return 1;
  // else just sort by created at
  return a.createdAt.getTime() - b.createdAt.getTime();
};

export function isOnboardInstance(
  onboardInstance: string | null,
): onboardInstance is OnboardInstance | null {
  return ["Production", "Stage", "QA", "Local", null].includes(onboardInstance);
}

export function assertIsOnboardInstance(
  onboardInstance: string | null,
): asserts onboardInstance is OnboardInstance | null {
  if (!isOnboardInstance(onboardInstance)) {
    throw new Error(`Invalid onboard instance: ${onboardInstance}`);
  }
}

export function getIsWellthyValuedAddedServiceAvailable(client: Client): boolean {
  const relevantPolicies = client.policies.filter((p) => {
    return (
      p.slfCoverages &&
      (p.slfCoverages.includes("Basic Life") || p.slfCoverages.includes("Voluntary Life"))
    );
  });
  const currentPolicyEffectiveDates = relevantPolicies.map((p) => p.policyEffective);
  const initialPolicyEffectiveDates = relevantPolicies.map((p) => p.initialPolicyEffectiveDate);
  const wellthyCutoverDate = new Date("2026-01-01");
  const metCurrentPolicyEffectiveDate = currentPolicyEffectiveDates.some(
    (date) => date && date >= wellthyCutoverDate,
  );
  const metInitialPolicyEffectiveDate = initialPolicyEffectiveDates.some(
    (date) => date && date >= wellthyCutoverDate,
  );

  return metCurrentPolicyEffectiveDate && metInitialPolicyEffectiveDate;
}

const DENTAL_COVERAGES = new Set<SlfCoverageLongName>(["Dental", "Dental (DHMO)", "ASO Dental"]);
export const getClientHasDental = (client: Client): boolean =>
  client.allPoliciesSlfCoverages?.some((coverage) => DENTAL_COVERAGES.has(coverage)) ?? false;

export const reasonForSelectingSunLife = [
  "Claims management",
  "Consolidating carriers",
  "Cost",
  "Digital capabilities",
  "Network and provider availability",
  "Product offering",
  "Service experience",
  "Other",
] as const;

export type ReasonForSelectingSunLife = ValueOf<typeof reasonForSelectingSunLife>;

export function getIsReasonForSelectingSunLife(item: string): item is ReasonForSelectingSunLife {
  // eslint-disable-next-line @typescript-eslint/consistent-type-assertions
  const isIt = reasonForSelectingSunLife.includes(item as ReasonForSelectingSunLife);
  return isIt;
}

export type EmployeeCertificateOptionType = "ONE_AND_SPLIT" | "SPLIT_ONLY";

export const employeeCertificateConfigByType: Record<
  QPSSoldCaseCategoryGroupCode,
  EmployeeCertificateOptionType
> = {
  AI: "ONE_AND_SPLIT",
  BASIC: "SPLIT_ONLY",
  HI: "ONE_AND_SPLIT",
  CANCER: "ONE_AND_SPLIT",
  DENTAL: "SPLIT_ONLY",
  VOL: "SPLIT_ONLY",
  FLI: "SPLIT_ONLY",
  SLFLVSOL: "SPLIT_ONLY",
  SLFSRVCS: "SPLIT_ONLY",
  CRITILL: "ONE_AND_SPLIT",
  DENTLPPO: "ONE_AND_SPLIT",
  DENTLPRE: "ONE_AND_SPLIT",
  ML: "ONE_AND_SPLIT",
  COMDIS: "SPLIT_ONLY",
  SL: "ONE_AND_SPLIT",
  VISION: "ONE_AND_SPLIT",
  SVADD: "SPLIT_ONLY",
};
